import React from "react";
import { Link } from "react-router-dom";
import { Disc3, BookOpen, ArrowRight, ShieldCheck, UserRound, FileMusic } from "lucide-react";

const options = [
  {
    title: "Yunah’Shach’Qal Yashar’al Records",
    subtitle: "Record Label",
    description: "Our music label workspace serves artists and the catalog of sound recordings, releases, and master-side agreements.",
    items: ["Recording artist profiles", "Master recordings and releases", "Label agreements and recording rights references"],
    path: "/music/records",
    icon: Disc3,
    testid: "public-records-portal",
  },
  {
    title: "Yunah’Shach’Qal Yashar’al Publishing",
    subtitle: "Music Publishing",
    description: "Our music publishing workspace serves writers and composers, composition registrations, writing shares, and publishing administration.",
    items: ["Songwriter and composer profiles", "Musical compositions and writer split sheets", "Publishing agreements and composition rights references"],
    path: "/music/publishing",
    icon: FileMusic,
    testid: "public-publishing-portal",
  },
];

export default function MusicOrganizations() {
  return <div className="min-h-screen bg-parchment" data-testid="music-organizations-public">
    <section className="bg-royal-blue px-4 py-12 text-white sm:px-6 sm:py-16">
      <div className="mx-auto max-w-6xl">
        <p className="text-sm font-semibold uppercase tracking-widest text-kingdom-gold">Music Organization</p>
        <h1 className="mt-3 font-heading text-3xl font-bold sm:text-4xl">Records &amp; Publishing</h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/85">Two distinct areas of our music organization: sound recordings and the written compositions behind them. Each has its own rights, records, and private administration.</p>
        <Link to="/members" className="mt-6 inline-flex items-center gap-2 rounded-md bg-kingdom-gold px-5 py-3 font-semibold text-royal-blue" data-testid="music-public-account-access">
          <UserRound size={18}/> Members &amp; Account Access <ArrowRight size={17}/>
        </Link>
      </div>
    </section>
    <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="grid gap-5 lg:grid-cols-2">
        {options.map(({title, subtitle,description, items,path,icon:Icon,testid}) =>
          <section key={path} className="flex min-w-0 flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
            <Icon className="h-10 w-10 text-royal-blue" aria-hidden="true"/>
            <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-slate-500">{subtitle}</p>
            <h2 className="mt-2 font-heading text-2xl font-semibold text-royal-blue">{title}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">{description}</p>
            <ul className="my-5 list-disc space-y-2 pl-5 text-sm text-slate-700">
              {items.map(item=><li key={item}>{item}</li>)}
            </ul>
            <Link to={path} data-testid={testid} className="mt-auto inline-flex items-center justify-center gap-2 rounded-md bg-royal-blue px-4 py-3 text-sm font-semibold text-white hover:bg-blue-800">
              Open Private {subtitle} Portal <ArrowRight size={16}/>
            </Link>
            <p className="mt-2 text-xs text-slate-500">Requires an existing account with this music workspace assigned.</p>
          </section>)}
      </div>
      <section className="mt-8 flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-5">
        <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-royal-blue"/>
        <div>
          <h2 className="font-semibold text-slate-900">Separate rights and responsibilities</h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">A master recording and a composition are not the same property. Label or publishing access does not itself transfer ownership, create a signed agreement, or authorize royalty collection. Rights depend on documented arrangements.</p>
        </div>
      </section>
    </main>
  </div>;
}
